import { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import React, { useEffect, useRef } from 'react';
import { Animated, LayoutChangeEvent, Platform, Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Text from '../../v2BaseComponents/Text';
import { colors } from '../../../utils/theme';
import { TabConfig, TabName, consumerAppMobileTabConfig, professionalAppTabConfig } from './tabConfig';
import Icon from '../../v2BaseComponents/Icon';
import Stack from '../../v2BaseComponents/Stack';
import { useForceUpdate } from '../../../hooks/useForceUpdate';
import { LinearGradient } from 'expo-linear-gradient';
import { hexToRgbA } from '../../../utils/hexToRGBA';
import { useIsKeyboardRestrictedView } from '../../shared/KeyboardRestrictedView';
import { useAppType } from '../../../hooks/useAppType';
import { TAB_BAR_HEIGHT } from './constants';

export type TabBarOwnProps = {};

export type TabBarProps = TabBarOwnProps & BottomTabBarProps;

const getTabConfig = (tabName: string, tabConfigList: TabConfig[]): TabConfig | null => {
    return tabConfigList.find((tab) => tab.name === tabName) || null;
};

type LayoutEventMap = Record<TabName | 'root', LayoutChangeEvent['nativeEvent']['layout'] | null>;
const layoutEventMap: LayoutEventMap = {
    activity: null,
    notes: null,
    profiles: null,
    clients: null,
    account: null,
    root: null,
    resources: null,
    organization: null,
};

const TabBar: React.FC<TabBarProps> = ({ state, descriptors, navigation }) => {
    const appType = useAppType();
    const tabConfigList = appType === 'professional' ? professionalAppTabConfig : consumerAppMobileTabConfig;
    const isKeyboardRestrictedView = useIsKeyboardRestrictedView();
    const insets = useSafeAreaInsets();
    // Under Android edge-to-edge (Expo 55+), insets.bottom is the system gesture/navigation-bar inset.
    // The root SafeAreaView already pads the screen's bottom edge, which left the bar floating above the
    // gesture area. Extend the bar down into that padding (matching marginBottom) so it sits flush at the
    // bottom with its labels still above the gesture inset. iOS keeps its existing home-indicator spacing.
    const androidBottomInset = Platform.OS === 'android' ? insets.bottom : 0;
    const { forceUpdate } = useForceUpdate();
    const [layoutMap, setLayoutMap] = React.useState<LayoutEventMap>(layoutEventMap);
    const focusedTab = state.routes[state.index].name as TabName;
    const focusedTabLayout = layoutMap[focusedTab];

    const tabLeftAnimationRef = useRef<null | Animated.Value>(null);
    const overlayOpacityAnimationRef = useRef<Animated.Value>(new Animated.Value(1));
    useEffect(() => {
        if (focusedTab && focusedTabLayout && !tabLeftAnimationRef.current) {
            tabLeftAnimationRef.current = new Animated.Value(focusedTabLayout.x);
        }
    }, [focusedTab, focusedTabLayout]);
    useEffect(() => {
        if (focusedTabLayout && tabLeftAnimationRef.current) {
            Animated.sequence([
                Animated.parallel([
                    Animated.timing(overlayOpacityAnimationRef.current, {
                        toValue: 0,
                        duration: 0,
                        useNativeDriver: true,
                    }),
                    Animated.timing(tabLeftAnimationRef.current, {
                        toValue: focusedTabLayout.x,
                        duration: 200,
                        useNativeDriver: Platform.OS === 'web',
                    }),
                ]),
                Animated.timing(overlayOpacityAnimationRef.current, {
                    toValue: 1,
                    duration: 200,
                    useNativeDriver: true,
                }),
            ]).start();
            forceUpdate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedTabLayout?.x]);

    return (
        <View
            onLayout={(e) => {
                const layout = e?.nativeEvent?.layout;
                setLayoutMap((prev) => ({ ...prev, root: layout }));
            }}
            style={{
                flexDirection: 'row',
                backgroundColor: colors['White'],
                height: TAB_BAR_HEIGHT + androidBottomInset,
                paddingBottom: androidBottomInset,
                marginBottom: -androidBottomInset,
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                borderColor: colors['Primary'],
                borderTopWidth: 1,
                display: isKeyboardRestrictedView ? 'none' : undefined,
                overflow: 'hidden',
            }}
        >
            {state.routes.map((route, index) => {
                const { options } = descriptors[route.key];

                const isFocused = state.index === index;

                const onPress = () => {
                    const event = navigation.emit({
                        type: 'tabPress',
                        target: route.key,
                        canPreventDefault: true,
                    });

                    if (!isFocused && !event.defaultPrevented) {
                        navigation.navigate(route.name);
                    }
                };

                const onLongPress = () => {
                    navigation.emit({
                        type: 'tabLongPress',
                        target: route.key,
                    });
                };

                const tabConfig = getTabConfig(route.name, tabConfigList);

                if (!tabConfig) return null;

                return (
                    <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={options.tabBarAccessibilityLabel}
                        testID={`navigation-tab-${route.name}`}
                        onPress={onPress}
                        onLongPress={onLongPress}
                        style={{ flex: 1, alignItems: 'center' }}
                        key={route.key}
                        onLayout={(e) => {
                            const layout = e?.nativeEvent?.layout;
                            setLayoutMap((prev) => ({ ...prev, [route.name]: layout }));
                        }}
                    >
                        <Stack
                            vertical
                            align="center"
                            justify="flex-start"
                            space={0}
                            style={{ height: TAB_BAR_HEIGHT, paddingTop: 10 }}
                        >
                            <Icon
                                name={tabConfig.iconName}
                                size={'tab-icon'}
                                colorVariant="Dark-Text"
                                color="Primary"
                            />
                            <Text variant="tab-text">{tabConfig.bottomTabLabel.toUpperCase()}</Text>
                        </Stack>
                    </Pressable>
                );
            })}
            {focusedTabLayout && tabLeftAnimationRef.current ? (
                <Animated.View
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: tabLeftAnimationRef.current,
                        width: focusedTabLayout?.width,
                        height: TAB_BAR_HEIGHT - 10,
                        bottom: 0,
                    }}
                    pointerEvents={'none'}
                >
                    <Animated.View
                        style={{
                            width: '100%',
                            top: 0,
                            height: '100%',
                            position: 'absolute',
                            opacity: overlayOpacityAnimationRef.current,
                        }}
                    >
                        <LinearGradient
                            colors={[
                                hexToRgbA(colors['Tab-Gradient-Start'], 0.7),
                                hexToRgbA(colors['Tab-Gradient-End'], 0),
                            ]}
                            style={{
                                width: '100%',
                                height: '96.5%',
                            }}
                        />
                    </Animated.View>
                    <View
                        style={{
                            height: 6,
                            width: '100%',
                            backgroundColor: colors['Primary-Reg'],
                        }}
                    />
                </Animated.View>
            ) : null}
        </View>
    );
};

export default React.memo(TabBar);
